import { useCallback, useContext } from 'react';

import useScriptLoader, { SCRIPTS, scriptsCtx } from '../../context/ScriptLoader/useScriptLoader';

interface IVerseEvents {
  onHotspotClick: (cb: () => void) => void;
  changeUserId: (id: string) => void;
  changeVideoDuration: (duration: number | null, title?: string, name?: string) => void;
  onVideoClose: () => void;
}

const useVerse = (contentWindow?) => {
  const isVerseLoaded = useScriptLoader(SCRIPTS.VERSE);
  const { state } = useContext(scriptsCtx);
  const verseEvents = state[SCRIPTS.VERSE] as IVerseEvents | null;

  const callIfVerseEvents = useCallback(
    (cb: () => void) => {
      if (verseEvents) {
        try {
          cb();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error);
        }
      }
    },
    [verseEvents],
  );

  const onHotspotClick = useCallback(
    (cb) => {
      callIfVerseEvents(() => verseEvents?.onHotspotClick(cb));
    },
    [verseEvents, callIfVerseEvents],
  );

  const onVideoClose = useCallback(() => {
    callIfVerseEvents(() => verseEvents?.onVideoClose());
  }, [verseEvents, callIfVerseEvents]);

  const changeUserId = useCallback(
    (id: string) => {
      callIfVerseEvents(() => verseEvents?.changeUserId(id));
    },
    [verseEvents, callIfVerseEvents],
  );

  const changeVideoDuration = useCallback(
    (duration: number | null, title?: string, name?: string) => {
      callIfVerseEvents(() => verseEvents?.changeVideoDuration(duration, title, name));
    },
    [verseEvents, callIfVerseEvents],
  );

  const callIfLoaded = useCallback(
    (cb: () => void) => {
      if (isVerseLoaded && contentWindow) {
        try {
          cb();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error);
        }
      }
    },
    [isVerseLoaded, contentWindow],
  );

  const goToChapter = useCallback(
    (index: string | number) => {
      callIfLoaded(() => contentWindow.postMessage({ action: 'goToChapter', index: index }, '*'));
    },
    [contentWindow, callIfLoaded],
  );

  const play = useCallback(() => {
    callIfLoaded(() => contentWindow.postMessage({ action: 'play' }, '*'));
  }, [contentWindow, callIfLoaded]);

  const seek = useCallback(
    (time) => {
      callIfLoaded(() => contentWindow.postMessage({ action: 'seek', time: time }, '*'));
    },
    [contentWindow, callIfLoaded],
  );

  return {
    goToChapter,
    play,
    seek,
    onHotspotClick,
    onVideoClose,
    changeUserId,
    changeVideoDuration,
  };
};

export default useVerse;
